
import { useState, useEffect } from 'react';
import { tokenService } from '../services/tokenService';
import { useAuth } from '../context/AuthContext';

export const useTokens = () => {
  const [tokenBalance, setTokenBalance] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false); // NEW: Track if we've loaded initial data
  const { user } = useAuth();

  useEffect(() => {
    if (!user) {
      setTokenBalance(0);
      setLoading(false);
      setIsInitialized(true); // Mark as initialized even without user
      return;
    }

    const fetchTokenBalance = async () => {
      try {
        setLoading(true);
        const balance = await tokenService.getUserTokenBalance();
        console.log('💰 useTokens: Initial balance loaded:', balance);
        setTokenBalance(balance);
        setError(null);
        setIsInitialized(true); // Mark as initialized after first successful load
      } catch (err) {
        console.error('Error fetching token balance:', err);
        setError('Failed to load token balance');
        setIsInitialized(true); // Mark as initialized even on error
      } finally {
        setLoading(false);
      }
    };

    fetchTokenBalance();

    // Subscribe to real-time updates
    const unsubscribe = tokenService.subscribeToTokenBalance((newBalance) => {
      console.log('💰 Token balance updated in hook:', newBalance);
      setTokenBalance(newBalance);
    });

    return unsubscribe;
  }, [user]);

  const refreshBalance = async () => {
    if (!user) return;
    
    try {
      const balance = await tokenService.getUserTokenBalance();
      setTokenBalance(balance);
    } catch (err) {
      console.error('Error refreshing token balance:', err);
    }
  };

  const hasEnoughTokens = (requiredTokens: number) => {
    return tokenBalance >= requiredTokens;
  };

  const isLowBalance = () => {
    return tokenBalance < 500; // Reverted back to 500 tokens
  };

  // NEW: Only consider tokens actually depleted if we've initialized and balance is 0
  const isTokenDepleted = () => {
    return isInitialized && tokenBalance <= 0;
  };

  return {
    tokenBalance,
    loading,
    error,
    isInitialized, // NEW: Expose initialization state
    isTokenDepleted, // NEW: Proper depletion check
    refreshBalance,
    hasEnoughTokens,
    isLowBalance
  };
};
